import axios from 'axios'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import urlJoin from 'url-join'
import config from '../../config'
import log from '../../log'
import {
  getContactEmail,
  extractResourceTypeData
} from '../../utilities/apiHelpers'
import { getAssetOrigin, getScaledImgUrl } from '../../utilities/linkHelpers'
import {
  formatCitations,
  formatDefinitionTableData,
  formatDataUsed,
  formatCaseStudies,
  formatArticleSections
} from '../formatters/article'
import { formatMetaTags, getIncludedUrls, getMediaImageAltText } from '../formatters/global'
import { getDatasetsWithMetaData } from '../api'
import simpleCacheBust from '../../utilities/simpleCacheBust'
import dayjs from 'dayjs'
import { addNotificationParamsIfEnabled, processNotificationData } from '../../utilities/datasetHelpers'
import { featureIsEnabled } from '../../stores/siteStore/siteStore'

const {
  apiSite,
  articleComponentTypes: {
    componentTypeCitations,
    componentTypeResources
  },
  displayContexts: {
    DATA_RESOURCE
  },
  useCacheBuster
} = config

export const getArticleStories = async (uuid) => {
  const filter = {
    filter: 'bundle',
    fieldPath: 'story_page'
  }
  try {
    const encodedFilter = encodeURIComponent(filter.filter)
    const encodedPath = encodeURIComponent(filter.fieldPath)
    const nonEncodedParams = `?include=field_ref_content_type&filter[${encodedFilter}]=${encodedPath}`

    const res = await axios({
      operationId: 'getArticleStories',
      method: 'get',
      baseURL: apiSite,
      jsonAPI: true,
      url: `/jsonapi/entity/references/${uuid}${nonEncodedParams}`,
      params: {
        'fields[node--story_page]': 'title,path,changed,field_ref_content_type',
        'fields[taxonomy_term--content_type]': 'name'
      }
    })

    const relatedStories = []
    res.data.map(eachStory => {
      const name = get(eachStory, 'title')
      const slug = get(eachStory, 'path.alias')
      const changed = get(eachStory, 'changed')
      const type = get(eachStory, 'fieldRefContentType.name')

      relatedStories.push({
        type: type,
        heading: name,
        date: changed && dayjs(changed).format('MMMM D, YYYY'),
        slug: slug
      })
    })

    return relatedStories
  } catch (error) {
    log.error('getArticleStories: request error', error)

    throw (error)
  }
}

/**
 * Get all datasets the param article has been attached to
 * @param {String} id article uuid to fetch against
 */
export const getArticleDatasets = async (id) => {
  // the three calls hit the same endpoint except for the filter name & path field
  const callParamsConfig = [
    {
      filter: 'dictionary-filter',
      fieldPath: 'field_dictionary.field_internal_page.id'
    },
    {
      filter: 'glossary-filter',
      fieldPath: 'field_glossary.field_internal_page.id'
    },
    {
      filter: 'methodology-filter',
      fieldPath: 'field_methodology.field_internal_page.id'
    },
    {
      filter: 'other-resources-filter',
      fieldPath: 'field_resources_additional.field_internal_page.id'
    }
  ]

  try {
    const results = await Promise.all(callParamsConfig.map(config => _getArticleDataset(id, config)))

    // aggregate every filter's results (incl. other-resources)
    const datasets = results.flat()
    /*
      must cross-reference all datasets for last updated date of newest version
      endpoints we called above do not guarantee the newest dataset version
    */
    const allDatasets = await getDatasetsWithMetaData()
    const encountered = []

    const reduced = datasets.reduce((prev, curr) => {
      const { name, slug } = curr

      // skip empties (e.g. a version node missing its field_dataset_type) and
      // collapse the same dataset returned across multiple versions
      if (!name || encountered.includes(name)) return prev

      // cache new values
      encountered.push(name)
      // find good last updated value
      const latest = allDatasets.find(dataset => dataset.name === name)

      const resource = {
        type: 'Dataset',
        heading: name,
        slug,
        date: latest?.last_updated ?? null // guard: prevents throw on name mismatch
      }

      return [...prev, resource]
    }, [])

    const final = reduced.sort((a, b) => new Date(b.date) - new Date(a.date))

    log.debug('getArticleDatasets: formatted response', final)

    return final
  } catch (error) {
    log.error('getArticleDatasets: request error', error)

    throw (error)
  }
}

/**
 * Reusable function to call the dataset version endpoint provided filter config
 * @param {String} id of current article/resource internal page uuid
 * @param {Object} config describes how to call the endpoint
 * @param {String} config.filter the filter name in the query
 * @param {String} config.fieldPath the path of the field assoc. with the filter
 */
const _getArticleDataset = async (id, { filter, fieldPath }) => {
  /*
    raw string prevents axios from encoding '=' which breaks filter call chain
      encode submitted variables regardless
  */
  const encodedFilter = encodeURIComponent(filter)
  const encodedPath = encodeURIComponent(fieldPath)
  const nonEncodedParams = `?include=field_dataset_type&filter[${encodedFilter}][condition][path]=${encodedPath}&filter[${encodedFilter}][condition][operator]=IN&filter[${encodedFilter}][condition][value][]=${id}`

  const res = await axios({
    operationId: 'getArticleDatasets',
    method: 'get',
    baseURL: apiSite,
    jsonAPI: true,
    url: `/jsonapi/node/dataset${nonEncodedParams}`
  })

  // name + path.alias live on the parent taxonomy_term/dataset (field_dataset_type),
  // not on the version node itself
  return res.data.map(dataset => {
    const name = get(dataset, 'fieldDatasetType.name')
    const slug = get(dataset, 'fieldDatasetType.path.alias')

    return {
      name,
      slug
    }
  })
}

/**
 * Get data dictionary data by unique identifier
 * @param {String} id uuid of the data dictionary to get
 * @param {Object} [opts] options object
 * @param {*} [opts.cancelToken] axios cancel token
 */
export const getDataDictionaryById = async (id, {
  cancelToken
} = {}) => {
  try {
    const { data, rawData } = await axios({
      operationId: 'getDataDictionaryById',
      method: 'get',
      baseURL: apiSite,
      jsonAPI: true,
      url: `/jsonapi/node/data_dictionary_page/${id}`,
      params: {
        include: [
          'field_ref_content_type',
          'field_ref_ancillary_file.field_media_file',
          'field_ref_contact_email'
        ].join(','),
        ...(useCacheBuster.getDataResourceById ? {
          cacheBuster: simpleCacheBust()
        } : {})
      },
      cancelToken
    })

    const internalNodeId = get(data, 'drupalInternalNid', null)
    const orderedIds = rawData?.data?.relationships?.field_dictionary_terms?.data?.map((i) => {
      return i.id
    })

    const pageContentType = get(data, 'fieldRefContentType.name', null)
    const lastUpdated = get(data, 'fieldLastUpdatedDate', null)
    const title = data.title || ''
    const rawFileDownloadUrl = get(data, 'fieldRefAncillaryFile.fieldMediaFile.uri.url', null)
    const fileDownloadUrl = rawFileDownloadUrl ? urlJoin(getAssetOrigin(), rawFileDownloadUrl) : null
    const contactEmail = getContactEmail(data)

    const formattedData = {
      title,
      pageContentType,
      lastUpdated,
      fileDownloadUrl,
      sections: [{
        id: '0',
        header: '',
        content: []
      }],
      contactEmail,
      metaTags: formatMetaTags(get(data, 'metatag')),
      internalNodeId,
      orderedIds
    }

    return formattedData
  } catch (error) {
    log.error('getDataDictionaryById: request error', error)
    throw (error)
  }
}

export const getDataDictionaryTermsByNodeId = async (internalNodeId, paginatedUrl = '', isGlossary = false, {
  cancelToken
} = {}) => {
  try {
    const { data, rawData } = await axios({
      operationId: 'getDataDictionaryTermsByNodeId',
      method: 'get',
      baseURL: apiSite,
      jsonAPI: true,
      url: paginatedUrl || `/jsonapi/paragraph/${isGlossary ? 'glossary_term' : 'dictionary_term'}?filter[parent_id]=${internalNodeId}`,
      params: {
        ...(isGlossary ? {} : {
          include: [
            'field_dictionary_term_table',
            'field_dict_term_opt_items'
          ].join(',')
        }),
        ...(useCacheBuster.getDataResourceById ? {
          cacheBuster: simpleCacheBust()
        } : {})
      },
      cancelToken
    })
    const nextUrl = rawData?.links?.next?.href

    let mappedTerms = data.map((term = {}) => {
      const {
        fieldDictionaryTermDefinition: definition = '',
        fieldDictionaryTermFullName: fullName = '',
        fieldDictionaryTermShortName: variableName = '',
        fieldDictionaryTermTable: rawTable,
        fieldDictTermOptItems: optItems,
        fieldDictionaryTermFootnote: footnote = '',
        id
      } = term

      const sources = optItems?.map(item => {
        const { fieldDictTermOptItemType: key, fieldDictTermOptItemValue: value } = item

        return {
          [key]: value
        }
      })
      const table = isEmpty(rawTable) ? undefined : formatDefinitionTableData(rawTable)

      return {
        type: 'dictionary',
        fullName,
        variableName,
        table,
        definition,
        footnote,
        sources,
        id
      }
    })
    const newTerms = nextUrl ? await getDataDictionaryTermsByNodeId(data.internalNodeId, nextUrl, isGlossary) : []

    mappedTerms = [...mappedTerms, ...newTerms]
    return mappedTerms
  } catch (error) {
    log.error('getDataDictionaryTermsByNodeId: request error', error)
    throw (error)
  }
}

export const getDataResourceById = async (id, {
  cancelToken
} = {}) => {
  try {
    const { data, rawData } = await axios({
      operationId: 'getDataResourceById',
      method: 'get',
      baseURL: apiSite,
      jsonAPI: true,
      url: `/jsonapi/node/data_resource_page/${id}`,
      params: {
        include: [
          'field_ref_content_type',
          'field_ref_ancillary_file',
          'field_ref_ancillary_file.field_media_file',
          'field_ref_data_resource_section',
          'field_ref_data_resource_section.field_data_resource_component',
          'field_ref_data_resource_section.field_data_resource_component.field_list_item',
          'field_ref_data_resource_section.field_data_resource_component.field_media_image.field_media_image',
          'field_ref_data_resource_section.field_data_resource_component.field_ref_resource.field_internal_page',
          'field_ref_data_resource_section.field_data_resource_component.field_ref_resource.field_internal_page.field_ref_content_type',
          'field_ref_data_resource_section.field_data_resource_component.field_ref_resource.field_ref_dataset_type',
          'field_ref_data_resource_section.field_data_resource_component.field_ref_resource.field_ancillary_file.field_media_file',
          'field_ref_data_resource_section.field_data_resource_component.field_ref_resource.field_ancillary_file.field_ref_content_type',
          'field_story_page_resources.field_internal_page',
          'field_story_page_resources.field_internal_page.field_ref_content_type',
          'field_story_page_resources.field_ancillary_file.field_media_file',
          'field_story_page_resources.field_ancillary_file.field_ref_content_type',
          'field_story_page_resources.field_ref_dataset_type',
          'field_ref_citations.field_ref_resource.field_internal_page',
          'field_ref_citations.field_ref_resource.field_ref_dataset_type',
          'field_ref_citations.field_ref_resource.field_ancillary_file.field_media_file',
          'field_ref_contact_email',
          'field_ref_data_resource_section'
        ].join(','),
        ...(useCacheBuster.getDataResourceById ? {
          cacheBuster: simpleCacheBust()
        } : {})
      },
      cancelToken
    })

    const included = rawData.included
    const citations = get(data, 'fieldRefCitations')
    const rawSections = get(data, 'fieldRefDataResourceSection')
    const hasFileDownload = !!(get(data, 'fieldRefAncillaryFile'))

    const formattedSections = formatArticleSections(rawSections, included, 'fieldDataResourceComponent')

    // setup citations section so it will be picked up by nav
    if (!isEmpty(citations)) {
      formattedSections.push({
        id: 'references',
        header: 'References',
        label: 'References',
        content: [{
          type: componentTypeCitations,
          citations: formatCitations(citations)
        }]
      })
    }

    // setup resources section so it will be picked up by nav
    const formattedResources = get(data, 'fieldStoryPageResources', []).map(rawResource => extractResourceTypeData(rawResource))

    if (!isEmpty(formattedResources)) {
      formattedSections.push({
        id: 'related-resources',
        content: [
          {
            displayContext: DATA_RESOURCE,
            type: componentTypeResources,
            resources: formattedResources
          }
        ],
        header: null,
        label: 'Related Resources'
      })
    }

    const formattedData = {
      id: get(data, 'id'),
      lastUpdated: get(data, 'fieldLastUpdatedDate'),
      created: get(data, 'created'),
      title: get(data, 'title'),
      pageContentType: get(data, 'fieldRefContentType.name'),
      publishedBy: get(data, 'fieldPublishedBy'),
      sections: formattedSections,
      hasFileDownload: hasFileDownload,
      fileDownloadUrl: hasFileDownload ? `${getAssetOrigin()}${get(data, 'fieldRefAncillaryFile.fieldMediaFile.uri.url')}` : null,
      fileDownloadName: hasFileDownload ? get(data, 'fieldRefAncillaryFile.fieldMediaFile.filename') : null,
      contactEmail: getContactEmail(data),
      metaTags: formatMetaTags(get(data, 'metatag'))
    }

    log.debug('getDataResourceById: formatted data', formattedData)
    return formattedData
  } catch (error) {
    log.error('getDataResourceById: request error', error)
    throw (error)
  }
}

export const getStoryData = async (id, {
  cancelToken
} = {}) => {
  try {
    const results = await Promise.all([
      getStoryPageById(id, cancelToken),
      getRecentCaseStudies({
        cancelToken,
        idToExclude: id
      })
    ])
    const final = {
      ...results[0],
      recentCaseStudies: results[1]
    }

    log.debug('getStoryData: formatted data', final)

    return final
  } catch (error) {
    log.error('getStoryData: request error', error)
    throw (error)
  }
}

/**
 * Get the ten most recent case studies published for display
 * @param {string} idToExclude - id to exclude from results (e.g., for excluding the current story page)
 */
export const getRecentCaseStudies = async ({ cancelToken, idToExclude = '' } = {}) => {
  try {
    const RESULTS_REQUESTED = 10
    const { data, rawData } = await axios({
      method: 'get',
      operationId: 'getRecentCaseStudies',
      jsonAPI: true,
      baseURL: apiSite,
      url: '/jsonapi/node/story_page',
      params: {
        include: 'field_ref_image.field_media_image',
        'filter[field_ref_content_type.name]': 'Case Study',
        'filter[excludeId][condition][path]': 'id',
        'filter[excludeId][condition][operator]': '<>',
        'filter[excludeId][condition][value]': idToExclude,
        'page[limit]': RESULTS_REQUESTED,
        sort: '-changed',
        ...(useCacheBuster.getRecentCaseStudies ? {
          cacheBuster: simpleCacheBust()
        } : {})
      },
      cancelToken
    })

    return formatCaseStudies(data, rawData.included)
  } catch (error) {
    log.error('getRecentCaseStudies: request error', error)
    throw (error)
  }
}

/**
 * Get story page data by unique identifier
 * @param {String} id uuid of the story page to get
 * @param {Object} [opts] options object
 * @param {*} [opts.cancelToken] axios cancel token
 */
export const getStoryPageById = async (id, {
  cancelToken
} = {}) => {
  try {
    // Create base include array without notification fields
    const includeFields = [
      'field_ref_content_type',
      'field_data_used.field_ref_resource.field_ref_dataset_type',
      'field_ref_story_page_section',
      'field_ref_story_page_section.field_story_page_component',
      'field_ref_story_page_section.field_story_page_component.field_list_item',
      'field_ref_story_page_section.field_story_page_component.field_ref_faq',
      'field_ref_story_page_section.field_story_page_component.field_media_image.field_media_image',
      'field_ref_story_page_section.field_story_page_component.field_ref_number_callout',
      'field_ref_story_page_section.field_story_page_component.field_ref_resource.field_ancillary_file.field_media_file',
      'field_ref_story_page_section.field_story_page_component.field_ref_resource.field_ancillary_file.field_ref_content_type',
      'field_ref_story_page_section.field_story_page_component.field_ref_resource.field_internal_page.field_ref_content_type',
      'field_ref_story_page_section.field_story_page_component.field_ref_call_to_action.field_internal_page',
      'field_ref_story_page_section.field_story_page_component.field_ref_call_to_action.field_ancillary_file.field_media_file',
      'field_ref_citations.field_ref_resource.field_internal_page',
      'field_ref_citations.field_ref_resource.field_ref_dataset_type',
      'field_ref_citations.field_ref_resource.field_ancillary_file.field_media_file',
      'field_ref_ancillary_file.field_media_file',
      'field_ref_image.field_media_image',
      'field_story_page_resources.field_internal_page',
      'field_story_page_resources.field_internal_page.field_ref_content_type',
      'field_story_page_resources.field_ancillary_file.field_media_file',
      'field_story_page_resources.field_ancillary_file.field_ref_content_type',
      'field_story_page_resources.field_ref_dataset_type',
      'field_ref_contact_email',
      'field_navigational_topic',
      'field_navigational_topic.parent',
      'field_navigational_topic.parent.field_ref_navigational_category',
      'field_navigational_topic.field_ref_navigational_category'
    ]

    // Create base params object
    const params = {}

    // Conditionally add notification fields if the feature is enabled
    addNotificationParamsIfEnabled(params, includeFields)

    const { data, rawData } = await axios({
      operationId: 'getStoryPageById',
      method: 'get',
      baseURL: apiSite,
      jsonAPI: true,
      url: `/jsonapi/node/story_page/${id}`,
      params: {
        ...params,
        include: includeFields.join(','),
        ...(useCacheBuster.getStoryPageById ? {
          cacheBuster: simpleCacheBust()
        } : {})
      },
      cancelToken
    })
    const included = rawData.included
    const citations = get(data, 'fieldRefCitations')
    const rawSections = get(data, 'fieldRefStoryPageSection')
    const hasFileDownload = !!(get(data, 'fieldRefAncillaryFile'))

    // Include notification params in response if notification feature is enabled.
    if (featureIsEnabled('datasetHighlight') && rawData.included) {
      data.notificationParams = processNotificationData(data, rawData)
    }

    const formattedSections = formatArticleSections(rawSections, included, 'fieldStoryPageComponent')

    // setup citations section so that it will be picked up by nav
    if (!isEmpty(citations)) {
      formattedSections.push({
        id: 'references',
        header: 'References',
        label: 'References',
        content: [{
          type: componentTypeCitations,
          citations: formatCitations(citations)
        }]
      })
    }

    const formattedResources = get(data, 'fieldStoryPageResources', []).map(rawResource => extractResourceTypeData(rawResource))

    const formattedData = {
      id: get(data, 'id'),
      lastUpdated: get(data, 'changed'),
      created: get(data, 'created'),
      title: get(data, 'title'),
      contentType: get(data, 'fieldRefContentType.name'),
      heroImageUrl: getScaledImgUrl('large', getIncludedUrls(included), get(data, 'fieldRefImage.fieldMediaImage.uri.url', '')),
      heroImageAltText: getMediaImageAltText(get(data, 'fieldRefImage'), included),
      description: get(data, 'fieldDescription'),
      publishedBy: get(data, 'fieldPublishedBy'),
      sections: formattedSections,
      included,
      resources: formattedResources,
      fileDownloadUrl: hasFileDownload ? `${getAssetOrigin()}${get(data, 'fieldRefAncillaryFile.fieldMediaFile.uri.url')}` : null,
      fileDownloadName: hasFileDownload ? get(data, 'fieldRefAncillaryFile.fieldMediaFile.filename') : null,
      contactEmail: getContactEmail(data),
      dataUsed: formatDataUsed(get(data, 'fieldDataUsed')),
      metaTags: formatMetaTags(get(data, 'metatag')),
      hideLeftNav: get(data, 'flagHideLeftNav'),
      datasetHighlightProps: get(data, 'notificationParams'),
      fieldNavigationalTopic: get(data, 'fieldNavigationalTopic')
    }

    return formattedData
  } catch (error) {
    log.error('getStoryPageById: request error', error)
    throw (error)
  }
}

/**
 * Get the tools that refrence the page in the featured resources section
 * @param {string} uuid - uuid of resource
 * @param {string} type - one of featured or additional resources
 */
export const getFeaturedResourceTools = async (uuid, type, { cancelToken } = {}) => {
  let field = ''
  if (type === 'additional') {
    field = 'field_lookup_tool_addl_resources'
  } else if (type === 'featured') {
    field = 'field_lookup_tool_feat_resources'
  } else {
    return { error: 'invalid field' }
  }
  try {
    // const RESULTS_REQUESTED = 10
    const { data } = await axios({
      method: 'get',
      operationId: 'getRecentCaseStudies',
      jsonAPI: true,
      baseURL: apiSite,
      url: '/jsonapi/node/look_up_tool',
      params: {
        'filter[featured-resources-filter][condition][path]': `field_look_up_tool_resource_sec.${field}.field_internal_page.id`,
        'filter[featured-resources-filter][condition][operator]': 'IN',
        'filter[featured-resources-filter][condition][value]': `${uuid}`,
        ...(useCacheBuster.getFeaturedResourceTools ? {
          cacheBuster: simpleCacheBust()
        } : {})
      },
      cancelToken
    })

    const formattedData = data.map(el => {
      return {
        date: el?.changed && dayjs(el?.changed).format('MMMM D, YYYY'),
        heading: el?.title,
        slug: el?.path?.alias,
        type: 'Tool'
      }
    })

    return formattedData
  } catch (error) {
    log.error('getFeaturedResourceTools: request error', error)
    throw (error)
  }
}
